/**
 * 管理者 API サービス
 *
 * Cloud Functions の管理者 API を呼び出すためのサービスです。
 */

import { getAuthInstance } from "../lib/firebase";
import { ADMIN_API_BASE_URL } from "../constants/api";

/**
 * 管理者情報
 */
export interface Admin {
  uid: string;
  email: string;
  role: "super_admin" | "admin";
  createdAt?: string;
}

/**
 * 認証ヘッダーを取得
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("認証されていません");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * API エラーをハンドリング
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "不明なエラー" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * 管理者一覧を取得
 */
export async function fetchAdmins(): Promise<Admin[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(ADMIN_API_BASE_URL, { headers });
  const data = await handleResponse<{ admins: Admin[] }>(response);
  return data.admins;
}

/**
 * 管理者を作成
 */
export async function createAdmin(email: string, password: string): Promise<Admin> {
  const headers = await getAuthHeaders();
  const response = await fetch(ADMIN_API_BASE_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({ email, password }),
  });
  return handleResponse<Admin>(response);
}

/**
 * 管理者を削除
 */
export async function deleteAdmin(uid: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE_URL}/${uid}`, {
    method: "DELETE",
    headers,
  });
  await handleResponse<{ success: boolean }>(response);
}
